import { CheckboxChangeEventDetail } from "@ionic/core";
import { IonCheckbox, IonItem } from "@ionic/react";
import { ReactNode } from "react";
import { Control, Controller, FieldError, FieldPath, FieldValues } from "react-hook-form";

import Error from "@components/fields/Error";
import styles from "@components/fields/RadioGroup.module.css";
import RequiredFieldIndicator from "@components/fields/RequiredFieldIndicator";
import BodyCopy from "@components/typography/BodyCopy";
import InputLabel from "@components/typography/InputLabel";
import { clickCurrentTarget } from "@utils/keyboardUtils";

type Option = {
  label: string;
  value: string;
};

type Props<FormData extends FieldValues> = {
  control: Control<FormData>;
  disabled?: boolean;
  error: FieldError | undefined;
  label?: string | ReactNode;
  name: FieldPath<FormData>;
  options: Option[];
  required?: boolean;
};

const RadioGroup = <FormData extends FieldValues>({
  control,
  disabled = false,
  error,
  label,
  name,
  options,
  required = false
}: Props<FormData>) => (
  <>
    <div className={styles.labelSpacing}>
      {label && (
        <InputLabel>
          {label}
          {required && <RequiredFieldIndicator />}
        </InputLabel>
      )}
    </div>
    <div>
      <Controller
        control={control}
        name={name}
        render={({ field: { name, onBlur, onChange, value } }) => (
          <>
            {options.map(option => (
              <IonItem className={styles.wrapperItem} data-test="toggle-checkmark" key={option.value} lines="none">
                <IonCheckbox
                  checked={option.value === value}
                  className={styles.checkbox}
                  disabled={disabled}
                  key={option.value}
                  labelPlacement="end"
                  name={`${name}-${option.value}`}
                  onIonBlur={onBlur}
                  onIonChange={(event: CustomEvent<CheckboxChangeEventDetail>) => {
                    if (event.detail.checked) {
                      onChange(event.detail.value);
                    }
                  }}
                  onKeyUp={clickCurrentTarget}
                  slot="start"
                  tabIndex={0}
                  value={option.value}
                >
                  <BodyCopy>{option.label}</BodyCopy>
                </IonCheckbox>
              </IonItem>
            ))}
          </>
        )}
      />
    </div>
    {error && <Error error={error} />}
  </>
);

export default RadioGroup;
